import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, RefreshCw, TrendingUp, Users, MessageCircle, FileText, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const KEY_STORAGE = 'wol_analytics_key';
// Use sessionStorage (cleared on tab close) instead of localStorage to reduce XSS exposure
// of the analytics passcode. Even better would be httpOnly cookies, but that requires a
// server-side session — overkill for this single-passcode admin gate.
const safeStorage = typeof window !== 'undefined' ? window.sessionStorage : null;

const SOURCE_LABELS = {
  hero: 'Hero Section',
  header: 'Header Button',
  mobile_header: 'Mobile Header',
  floating_button: 'Floating Button',
  whatsapp_cta_section: 'WA CTA Section',
  lookbook_wa_cta: 'Lookbook CTA',
  footer_cta: 'Footer CTA',
  footer_icon: 'Footer Icon',
  catalogue_bottom_cta: 'Catalogue Bottom',
  bulk_order_form: 'Bulk Order Form',
  page: 'Generic',
};

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className={`border p-6 ${accent ? 'bg-[#2D4A2D] border-[#2D4A2D]' : 'bg-[#F5F0E8] border-[#D5CCBF]'}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 flex items-center justify-center ${accent ? 'bg-[#C9A84C]/20' : 'bg-[#E8DED1]'}`}>
          <Icon size={18} strokeWidth={1.5} className={accent ? 'text-[#C9A84C]' : 'text-[#2D4A2D]'} />
        </div>
      </div>
      <div className={`font-serif text-3xl font-light leading-none mb-1 ${accent ? 'text-[#F5F0E8]' : 'text-[#2D4A2D]'}`}>
        {value ?? '—'}
      </div>
      <div className={`font-sans font-medium text-xs uppercase tracking-[0.15em] mb-0.5 ${accent ? 'text-[#C9A84C]' : 'text-[#2D4A2D]'}`}>
        {label}
      </div>
      {sub && <div className={`font-sans font-light text-xs ${accent ? 'text-[#F5F0E8]/60' : 'text-[#5C6B5C]'}`}>{sub}</div>}
    </motion.div>
  );
}

export default function AnalyticsPage() {
  const [key, setKey] = useState(() => safeStorage?.getItem(KEY_STORAGE) || '');
  const [inputKey, setInputKey] = useState('');
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchStats = useCallback(async (k) => {
    setLoading(true);
    setError('');
    try {
      const [sRes, eRes, qRes] = await Promise.all([
        fetch(`${API}/api/analytics/stats?key=${k}`),
        fetch(`${API}/api/analytics/events?key=${k}&limit=15`),
        fetch(`${API}/api/enquiries?key=${k}&limit=25`),
      ]);
      if (sRes.status === 401) { setError('Wrong passcode.'); setAuthed(false); return; }
      const sData = await sRes.json();
      const eData = await eRes.json();
      const qData = await qRes.json();
      setStats(sData);
      setEvents(Array.isArray(eData) ? eData : (eData.items || []));
      setEnquiries(qData.items || []);
      setAuthed(true);
      setLastRefresh(new Date());
      safeStorage?.setItem(KEY_STORAGE, k);
    } catch {
      setError('Could not connect. Check your connection.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) fetchStats(key);
    // Run once on mount with the initial persisted key, if any.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    setKey(inputKey);
    fetchStats(inputKey);
  };

  const maxSource = stats?.clicks_by_source?.[0]?.count || 1;

  return (
    <div className="min-h-screen bg-[#F5F0E8] pt-16 md:pt-20">
      {/* Page Header */}
      <div className="border-b border-[#D5CCBF] bg-[#F5F0E8] px-6 md:px-12 py-8">
        <div className="max-w-5xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-[#5C6B5C] text-sm font-sans hover:text-[#2D4A2D] transition-colors mb-5 group">
            <ArrowLeft size={15} strokeWidth={1.5} className="group-hover:-translate-x-1 transition-transform" />
            Back to Website
          </Link>
          <div className="flex items-start justify-between">
            <div>
              <span className="text-[10px] font-sans uppercase tracking-[0.3em] text-[#C9A84C] font-semibold block mb-1">Weaves of Lucknow</span>
              <h1 className="font-serif text-3xl md:text-4xl text-[#2D4A2D] font-light">Analytics</h1>
              <p className="font-sans font-light text-[#5C6B5C] text-sm mt-1">WhatsApp community clicks & enquiry tracking</p>
            </div>
            {authed && (
              <button
                onClick={() => fetchStats(key)}
                disabled={loading}
                className="flex items-center gap-2 border border-[#D5CCBF] px-4 py-2 text-[#2D4A2D] font-sans text-xs font-medium hover:border-[#2D4A2D] transition-colors disabled:opacity-50"
                data-testid="analytics-refresh-btn"
              >
                <RefreshCw size={13} strokeWidth={1.5} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
            )}
          </div>
          {lastRefresh && (
            <p className="font-sans text-xs text-[#5C6B5C]/60 mt-2">
              Last updated: {lastRefresh.toLocaleTimeString('en-IN')}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        {/* Login Gate */}
        {!authed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-sm mx-auto text-center py-16"
          >
            <div className="w-12 h-12 bg-[#2D4A2D] flex items-center justify-center mx-auto mb-6">
              <Lock size={20} strokeWidth={1.5} className="text-[#C9A84C]" />
            </div>
            <h2 className="font-serif text-2xl text-[#2D4A2D] mb-2">Private Dashboard</h2>
            <p className="font-sans font-light text-[#5C6B5C] text-sm mb-8">Enter your analytics passcode to continue</p>
            <form onSubmit={handleLogin} className="flex gap-3" data-testid="analytics-login-form">
              <input
                type="password"
                value={inputKey}
                onChange={e => setInputKey(e.target.value)}
                placeholder="Enter passcode"
                className="flex-1 border border-[#D5CCBF] px-4 py-3 text-sm font-sans text-[#1A2A1A] placeholder-[#5C6B5C]/50 focus:outline-none focus:border-[#2D4A2D] bg-transparent"
                data-testid="analytics-passcode-input"
              />
              <button
                type="submit"
                className="bg-[#2D4A2D] text-[#F5F0E8] px-5 py-3 font-sans text-sm font-medium hover:bg-[#1A2A1A] transition-colors"
                data-testid="analytics-login-btn"
              >
                Enter
              </button>
            </form>
            {error && <p className="mt-3 text-sm font-sans text-red-500">{error}</p>}
          </motion.div>
        )}

        {/* Dashboard */}
        {authed && stats && (
          <div className="space-y-10">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-stats">
              <StatCard icon={TrendingUp} label="Total WA Clicks" value={stats.total_wa_clicks} sub="All time" accent />
              <StatCard icon={Users} label="This Month" value={stats.month_wa_clicks} sub="Last 30 days" />
              <StatCard icon={MessageCircle} label="This Week" value={stats.week_wa_clicks} sub="Last 7 days" />
              <StatCard icon={FileText} label="Bulk Enquiries" value={stats.total_enquiries} sub="Form submissions" />
            </div>

            {/* Source Breakdown */}
            {stats.clicks_by_source?.length > 0 && (
              <div>
                <h2 className="font-serif text-2xl text-[#2D4A2D] font-light mb-6">Clicks by Source</h2>
                <div className="border border-[#D5CCBF] divide-y divide-[#E8DED1]">
                  {stats.clicks_by_source.map((row, i) => (
                    <motion.div
                      key={row.source}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.04 }}
                      className="flex items-center gap-5 px-6 py-4 bg-[#F5F0E8] hover:bg-[#E8DED1] transition-colors"
                    >
                      <div className="w-7 text-right font-sans text-xs text-[#5C6B5C] font-medium flex-shrink-0">
                        #{i + 1}
                      </div>
                      <div className="w-40 font-sans text-sm text-[#1A2A1A] font-medium flex-shrink-0">
                        {SOURCE_LABELS[row.source] || row.source}
                      </div>
                      <div className="flex-1 flex items-center gap-3">
                        <div className="flex-1 bg-[#E8DED1] h-2">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${(row.count / maxSource) * 100}%` }}
                            transition={{ delay: i * 0.06 + 0.3, duration: 0.6 }}
                            className="h-full bg-[#2D4A2D]"
                          />
                        </div>
                        <span className="font-serif text-lg text-[#2D4A2D] font-light w-8 text-right flex-shrink-0">
                          {row.count}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Daily 7-day */}
            {stats.daily_last_7?.length > 0 && (
              <div>
                <h2 className="font-serif text-2xl text-[#2D4A2D] font-light mb-6">Last 7 Days</h2>
                <div className="border border-[#D5CCBF] p-6 bg-[#F5F0E8]">
                  <div className="flex items-end gap-3 h-28">
                    {stats.daily_last_7.map((day, i) => {
                      const maxDay = Math.max(...stats.daily_last_7.map(d => d.count), 1);
                      const pct = (day.count / maxDay) * 100;
                      return (
                        <div key={day.date} className="flex-1 flex flex-col items-center gap-1.5">
                          <span className="font-sans text-xs text-[#2D4A2D] font-medium">{day.count}</span>
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: `${pct}%` }}
                            transition={{ delay: i * 0.07, duration: 0.5 }}
                            className="w-full bg-[#2D4A2D] min-h-[4px]"
                          />
                          <span className="font-sans text-[9px] text-[#5C6B5C] text-center">
                            {new Date(day.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Bulk Enquiries (Leads) */}
            {enquiries?.length > 0 && (
              <div data-testid="analytics-enquiries">
                <h2 className="font-serif text-2xl text-[#2D4A2D] font-light mb-6">Bulk Enquiries <span className="text-[#5C6B5C] text-base">({enquiries.length})</span></h2>
                <div className="border border-[#D5CCBF] divide-y divide-[#E8DED1]">
                  {enquiries.map((q, i) => (
                    <details key={q.id || i} className="bg-[#F5F0E8] hover:bg-[#E8DED1] transition-colors">
                      <summary className="cursor-pointer list-none px-6 py-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 min-w-0">
                          <span className="bg-[#C9A84C] text-[#1A2A1A] px-2 py-0.5 font-medium uppercase tracking-wider text-[9px] font-sans flex-shrink-0">Lead</span>
                          <span className="font-sans text-sm text-[#1A2A1A] font-medium truncate">{q.name}</span>
                          <span className="font-sans text-xs text-[#5C6B5C] truncate hidden sm:inline">· {q.business_name}</span>
                          <span className="font-sans text-xs text-[#5C6B5C] truncate hidden md:inline">· {q.city}</span>
                        </div>
                        <span className="font-sans text-xs text-[#5C6B5C] flex-shrink-0">
                          {new Date(q.timestamp).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </summary>
                      <div className="px-6 pb-5 pt-1 grid sm:grid-cols-2 gap-x-6 gap-y-2 font-sans text-xs">
                        <div><span className="text-[#5C6B5C] uppercase tracking-wider text-[10px]">Phone</span><div className="text-[#1A2A1A]">{q.phone}</div></div>
                        <div><span className="text-[#5C6B5C] uppercase tracking-wider text-[10px]">City</span><div className="text-[#1A2A1A]">{q.city}</div></div>
                        <div><span className="text-[#5C6B5C] uppercase tracking-wider text-[10px]">Business</span><div className="text-[#1A2A1A]">{q.business_name}</div></div>
                        <div><span className="text-[#5C6B5C] uppercase tracking-wider text-[10px]">Type</span><div className="text-[#1A2A1A]">{q.order_type}</div></div>
                        <div><span className="text-[#5C6B5C] uppercase tracking-wider text-[10px]">Fabric</span><div className="text-[#1A2A1A]">{q.preferred_fabric}</div></div>
                        <div><span className="text-[#5C6B5C] uppercase tracking-wider text-[10px]">Quantity</span><div className="text-[#1A2A1A]">{q.quantity}</div></div>
                        <div className="sm:col-span-2"><span className="text-[#5C6B5C] uppercase tracking-wider text-[10px]">Message</span><div className="text-[#1A2A1A] mt-1 whitespace-pre-wrap">{q.message}</div></div>
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Events */}
            {events?.length > 0 && (
              <div>
                <h2 className="font-serif text-2xl text-[#2D4A2D] font-light mb-6">Recent Activity</h2>
                <div className="border border-[#D5CCBF] divide-y divide-[#E8DED1]">
                  {events.map((ev, i) => (
                    <div key={ev.id || i} className="flex items-center justify-between px-6 py-3.5 bg-[#F5F0E8] text-xs font-sans">
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-0.5 font-medium uppercase tracking-wider text-[9px] ${
                          ev.event_type === 'whatsapp_click'
                            ? 'bg-[#2D4A2D] text-[#C9A84C]'
                            : 'bg-[#C9A84C] text-[#1A2A1A]'
                        }`}>
                          {ev.event_type === 'whatsapp_click' ? 'WA Click' : 'Enquiry'}
                        </span>
                        <span className="text-[#1A2A1A] font-medium">{SOURCE_LABELS[ev.source] || ev.source}</span>
                        <span className="text-[#5C6B5C]">{ev.page}</span>
                      </div>
                      <span className="text-[#5C6B5C]">
                        {new Date(ev.timestamp).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!stats.total_wa_clicks && !stats.total_enquiries && (
              <div className="text-center py-16 border border-[#D5CCBF]">
                <p className="font-sans font-light text-[#5C6B5C] text-sm">No events recorded yet. Clicks will appear here once users visit the site.</p>
              </div>
            )}

            <p className="font-sans text-xs text-[#5C6B5C]/50 text-center pb-4">
              Today: {stats.today_wa_clicks} WA clicks · Auto-tracks all WhatsApp CTA interactions
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
